import { motion } from 'framer-motion';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { Link2, ExternalLink } from 'lucide-react';
import { ReferrerData } from '@/hooks/useQRAnalytics';
import { Progress } from '@/components/ui/progress';

interface ReferrerSourcesProps {
  data: ReferrerData[];
}

const COLORS = [
  'hsl(var(--primary))',
  'hsl(280, 85%, 60%)',
  'hsl(320, 80%, 55%)',
  'hsl(200, 85%, 55%)',
  'hsl(150, 80%, 45%)',
  'hsl(45, 90%, 55%)',
];

const getSourceIcon = (source: string) => {
  switch (source) {
    case 'Direct':
      return '📱';
    case 'Facebook':
      return '📘';
    case 'Twitter/X':
      return '🐦';
    case 'Instagram':
      return '📷';
    case 'LinkedIn':
      return '💼';
    case 'TikTok':
      return '🎵';
    case 'Google':
      return '🔍';
    case 'Email':
      return '📧';
    case 'Website':
      return '🌐';
    default:
      return '🔗';
  }
};

const CustomTooltip = ({ active, payload }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-popover border border-border rounded-lg shadow-lg p-3">
        <p className="text-sm font-medium">
          {getSourceIcon(payload[0].name)} {payload[0].name}
        </p>
        <p className="text-sm text-muted-foreground">
          {payload[0].value} scans ({payload[0].payload.percentage}%)
        </p>
      </div>
    );
  }
  return null;
};

export function ReferrerSources({ data }: ReferrerSourcesProps) {
  const maxCount = Math.max(...data.map(d => d.count), 1);
  const pieData = data.map((d, i) => ({
    ...d,
    name: d.source,
    value: d.count,
    fill: COLORS[i % COLORS.length],
  }));

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.6 }}
      className="surface-elevated rounded-xl p-6"
    >
      <div className="flex items-center gap-2 mb-6">
        <Link2 className="h-5 w-5 text-primary" />
        <h3 className="text-lg font-semibold">Traffic Sources</h3>
      </div>

      {data.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          <ExternalLink className="h-8 w-8 mx-auto mb-2 opacity-50" />
          <p>No referrer data available yet</p>
          <p className="text-xs mt-1">Referrer data is captured when available</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-6">
          <div className="h-[200px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  innerRadius={40}
                  outerRadius={80}
                  paddingAngle={2}
                  dataKey="value"
                >
                  {pieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.fill} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          
          <div className="space-y-3">
            {data.map((source, index) => (
              <div key={source.source} className="space-y-1.5">
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <span>{getSourceIcon(source.source)}</span>
                    <span className="font-medium">{source.source}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-muted-foreground">{source.count}</span>
                    <span className="text-muted-foreground w-10 text-right">{source.percentage}%</span>
                  </div>
                </div>
                <Progress 
                  value={(source.count / maxCount) * 100} 
                  className="h-1.5"
                  style={{
                    ['--progress-foreground' as any]: COLORS[index % COLORS.length],
                  }}
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </motion.div>
  );
}
